import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Archive, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { CreateProjectModal } from '@/features/projects/components/CreateProjectModal';
import {
  ProjectCard,
  type ProjectCardMember,
  type ProjectCardStats,
} from '@/features/projects/components/ProjectCard';
import { ProjectListRow } from '@/features/projects/components/ProjectListRow';
import {
  type ProjectFilter,
  type ProjectSort,
  type ProjectViewMode,
  ProjectsToolbar,
} from '@/features/projects/components/ProjectsToolbar';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useQuickCreate } from '@/lib/hooks/useQuickCreate';
import { supabase } from '@/lib/supabase/client';

interface ProjectStatsRow {
  project_id: string;
  total: number;
  completed: number;
  overdue: number;
  due_soon: number;
}

interface ProjectRow {
  id: string;
  name: string;
  slug: string;
  identifier: string | null;
  color: string | null;
  icon: string | null;
  description: string | null;
  kind: string | null;
  created_at: string | null;
  updated_at: string | null;
  members: { user: ProjectCardMember | null }[];
}

export const Route = createFileRoute('/_app/$wsSlug/projects/')({
  component: ProjectsPage,
});

function ProjectsPage() {
  const { wsSlug } = useParams({ from: '/_app/$wsSlug/projects/' });
  const navigate = useNavigate();
  const { user } = useAuth();
  const myUserId = user?.id ?? '';

  const [modalOpen, setModalOpen] = useState(false);
  useQuickCreate(() => setModalOpen(true));

  // Toolbar state. We default to grid because the user is coming from the
  // legacy view; can flip to list per session.
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ProjectFilter>('all');
  const [sort, setSort] = useState<ProjectSort>('recent');
  const [viewMode, setViewMode] = useState<ProjectViewMode>('grid');

  const wsQuery = useQuery({
    queryKey: ['workspace', wsSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select('id, name, slug')
        .eq('slug', wsSlug)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const ws = wsQuery.data;

  const projectsQuery = useQuery({
    queryKey: ['projects-with-members', ws?.id],
    enabled: Boolean(ws?.id),
    queryFn: async () => {
      if (!ws?.id) return [] as ProjectRow[];
      const { data, error } = await supabase
        .from('projects')
        .select(
          'id, name, slug, identifier, color, icon, description, kind, created_at, updated_at, members:project_members(user:profiles(id, display_name, avatar_url))',
        )
        .eq('workspace_id', ws.id)
        .is('archived_at', null);
      if (error) throw error;
      return (data ?? []) as unknown as ProjectRow[];
    },
  });

  const statsQuery = useQuery({
    queryKey: ['project-stats', ws?.id],
    enabled: Boolean(ws?.id),
    queryFn: async () => {
      if (!ws?.id) return new Map<string, ProjectStatsRow>();
      const { data, error } = await supabase.rpc('project_stats_for_workspace', {
        p_workspace_id: ws.id,
      });
      if (error) throw error;
      const map = new Map<string, ProjectStatsRow>();
      for (const row of (data ?? []) as ProjectStatsRow[]) map.set(row.project_id, row);
      return map;
    },
  });

  // Derive a flat list with stats + members ready for filter/sort.
  type Enriched = {
    project: ProjectRow;
    stats: ProjectCardStats;
    members: ProjectCardMember[];
    iAmMember: boolean;
  };

  const enriched: Enriched[] = useMemo(() => {
    const rows = projectsQuery.data ?? [];
    return rows.map((p) => {
      const s = statsQuery.data?.get(p.id);
      const stats: ProjectCardStats = {
        total: Number(s?.total ?? 0),
        completed: Number(s?.completed ?? 0),
        overdue: Number(s?.overdue ?? 0),
        due_soon: Number(s?.due_soon ?? 0),
      };
      const members: ProjectCardMember[] = (p.members ?? [])
        .map((m) => m.user)
        .filter((u): u is ProjectCardMember => u !== null);
      return {
        project: p,
        stats,
        members,
        iAmMember: members.some((m) => m.id === myUserId),
      };
    });
  }, [projectsQuery.data, statsQuery.data, myUserId]);

  const visible = useMemo(() => {
    let list = enriched;

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((e) => {
        const name = e.project.name?.toLowerCase() ?? '';
        const id = e.project.identifier?.toLowerCase() ?? '';
        return name.includes(q) || id.includes(q);
      });
    }

    if (filter === 'with_overdue') list = list.filter((e) => e.stats.overdue > 0);
    else if (filter === 'mine') list = list.filter((e) => e.iAmMember);
    else if (filter === 'empty') list = list.filter((e) => e.stats.total === 0);

    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case 'name':
          return (a.project.name ?? '').localeCompare(b.project.name ?? '');
        case 'most_overdue':
          return (
            b.stats.overdue - a.stats.overdue ||
            (a.project.name ?? '').localeCompare(b.project.name ?? '')
          );
        case 'best_progress': {
          const pa = a.stats.total ? a.stats.completed / a.stats.total : 0;
          const pb = b.stats.total ? b.stats.completed / b.stats.total : 0;
          return pb - pa;
        }
        default: {
          const ta = a.project.updated_at ?? a.project.created_at ?? '';
          const tb = b.project.updated_at ?? b.project.created_at ?? '';
          return tb.localeCompare(ta);
        }
      }
    });

    return sorted;
  }, [enriched, search, filter, sort]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Proyectos</h1>
          <p className="mt-0.5 text-sm text-quiet">
            Click en un proyecto para abrir su tablero, lista, KPIs y configuración.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/$wsSlug/projects/archivados"
            params={{ wsSlug }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-quiet hover:text-ink hover:bg-accent-sf"
            title="Ver proyectos archivados"
          >
            <Archive size={14} />
            <span>Archivados</span>
          </Link>
          <Button onClick={() => setModalOpen(true)} disabled={!ws?.id}>
            <Plus size={14} />
            <span>Nuevo proyecto</span>
          </Button>
        </div>
      </div>

      <ProjectsToolbar
        search={search}
        onSearch={setSearch}
        filter={filter}
        onFilter={setFilter}
        sort={sort}
        onSort={setSort}
        viewMode={viewMode}
        onViewMode={setViewMode}
      />

      {projectsQuery.isLoading ? (
        <p className="text-sm text-quiet">Cargando proyectos...</p>
      ) : projectsQuery.isError ? (
        <p className="text-sm text-critical">No se pudieron cargar los proyectos.</p>
      ) : enriched.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
          <p className="text-sm text-quiet">
            Aún no hay proyectos en este workspace. Crea uno para empezar a organizar tareas.
          </p>
          <Button className="mt-4" onClick={() => setModalOpen(true)} disabled={!ws?.id}>
            <Plus size={14} />
            <span>Crear el primero</span>
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center">
          <p className="text-sm text-quiet">Ningún proyecto coincide con los filtros aplicados.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          {/* Movil (< sm): pills limpias con SOLO el nombre, lista vertical
              compacta para ver muchos proyectos sin scroll infinito y dejar el
              boton "Crear proyecto" a la vista. Navega al board igual que la card. */}
          <ul className="flex flex-col gap-2 sm:hidden">
            {visible.map((e) => (
              <li key={e.project.id}>
                <Link
                  to="/$wsSlug/projects/$projectSlug/board"
                  params={{ wsSlug, projectSlug: e.project.slug }}
                  className="flex min-h-[44px] w-full items-center rounded-lg border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:border-mute hover:bg-surface-elev"
                >
                  <span className="truncate">{e.project.name}</span>
                </Link>
              </li>
            ))}
          </ul>
          {/* Desktop/tablet (sm+): cards Variante B (sin cambios). */}
          <ul className="hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((e) => (
              <li key={e.project.id}>
                <ProjectCard
                  wsSlug={wsSlug}
                  project={e.project}
                  stats={e.stats}
                  members={e.members}
                  loading={statsQuery.isLoading}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="rounded-lg border border-border bg-surface overflow-hidden">
          <div className="grid items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-wide text-mute border-b border-border bg-surface-elev grid-cols-[20px_minmax(170px,300px)_minmax(0,1fr)_150px_72px_84px_116px]">
            <span aria-hidden />
            <span>Proyecto</span>
            <span aria-hidden />
            <span>Progreso</span>
            <span>Hechas</span>
            <span>Alertas</span>
            <span className="text-right">Equipo</span>
          </div>
          <ul>
            {visible.map((e) => (
              <li key={e.project.id}>
                <ProjectListRow
                  wsSlug={wsSlug}
                  project={e.project}
                  stats={e.stats}
                  members={e.members}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <CreateProjectModal
        workspaceId={ws?.id}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreated={(projectSlug) => {
          void navigate({
            to: '/$wsSlug/projects/$projectSlug/board',
            params: { wsSlug, projectSlug },
          });
        }}
      />
    </div>
  );
}
